import axios from "axios";
import axiosRetry from "axios-retry";
// Get the base URL based on environment
const getBaseURL = () => {
  if (process.env.NODE_ENV === "production") {
    // In production, use relative path since backend serves frontend
    return "/api";
  }
  // In development, check if we're running through nginx (port 80)
  if (window.location.port === "80" || window.location.port === "") {
    return "/api"; // nginx will proxy to backend
  }
  // Direct development mode (no nginx)
  return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:3001"}/api`;
};

// --- Concurrency queue: limits how many API requests run at the same time ---
function createRequestQueue(concurrency = 3) {
  const queue = [];
  let running = 0;

  function next() {
    while (running < concurrency && queue.length > 0) {
      running++;
      const { fn, resolve, reject } = queue.shift();
      fn()
        .then(resolve, reject)
        // eslint-disable-next-line no-loop-func
        .finally(() => {
          running--;
          next();
        });
    }
  }

  return function enqueue(fn) {
    return new Promise((resolve, reject) => {
      queue.push({ fn, resolve, reject });
      next();
    });
  };
}

const enqueue = createRequestQueue(3);
// --- End concurrency queue ---

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    "Content-Type": "application/json",
  },
});
// Per-request opt-out of the retry policy below, for requests that MOVE MONEY
// (Stripe authorize / capture, pay invoice). The policy replays a POST whose
// connection dropped with no response (isNetworkOrIdempotentRequestError), but
// the server keeps processing the first copy, so a replay is a second
// authorization — and on the invoice path a second charge (Oct 2026 review).
// Spread into the request config: api.post(url, body, { headers, ...NO_RETRY }).
// A dropped connection then surfaces as an error the user retries by hand,
// which the server's balance / externalId checks can stop.
export const NO_RETRY = { "axios-retry": { retries: 0 } };

// Configure global retry logic for 429 errors
axiosRetry(api, {
  retries: process.env.NODE_ENV === "production" ? 5 : 3, // More retries on Heroku
  retryDelay: (retryCount, error) => {
    // Use retry-after header if provided by server
    const retryAfter = error.response?.headers["retry-after"];
    if (retryAfter && error.response?.status === 429) {
      return parseInt(retryAfter) * 1000;
    }
    // More conservative backoff on production
    const baseDelay = process.env.NODE_ENV === "production" ? 2000 : 1000;
    return Math.min(baseDelay * Math.pow(2, retryCount), 30000); // Cap at 30s
  },
  retryCondition: (error) => {
    // Retry on 429 (rate limit) or network/idempotent errors
    return (
      error.response?.status === 429 ||
      axiosRetry.isNetworkOrIdempotentRequestError(error)
    );
  },
});
// Add request interceptors if needed
api.interceptors.request.use(
  (config) => {
    // You can modify requests here (e.g., add auth tokens)
    return config;
  },
  (error) => Promise.reject(error)
);
// Add response interceptors if needed
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle errors globally
    return Promise.reject(error);
  }
);

// Wrap api methods with concurrency queue so at most 3 requests run at once
const originalGet = api.get.bind(api);
const originalPost = api.post.bind(api);
const originalPut = api.put.bind(api);
const originalPatch = api.patch.bind(api);
const originalDelete = api.delete.bind(api);

api.get = (...args) => enqueue(() => originalGet(...args));
api.post = (...args) => enqueue(() => originalPost(...args));
api.put = (...args) => enqueue(() => originalPut(...args));
api.patch = (...args) => enqueue(() => originalPatch(...args));
api.delete = (...args) => enqueue(() => originalDelete(...args));

export default api;
